import { Track } from './Track';
import { Element } from './Element';
import { VideoElement } from './elements/VideoElement';
import { TextElement } from './elements/TextElement';
import { AudioElement } from './elements/AudioElement';
import { ImageElement } from './elements/ImageElement';
import { CompositionElement } from './elements/CompositionElement';
import { ShapeElement } from './elements/ShapeElement';

/**
 * Supported frame rates
 */
export enum Framerate {
  FPS_24 = 24,
  FPS_30 = 30,
  FPS_60 = 60,
}

/**
 * Timeline represents the entire project timeline containing tracks and metadata
 * Aligned with Creatomate properties and structure
 */
export class Timeline {
  // Private properties with readonly public access
  private _id: string = `timeline_${Date.now()}_${Math.random()
    .toString(36)
    .substr(2, 9)}`;
  private _tracks: Map<string, Track> = new Map();
  private _width: number = 1920;
  private _height: number = 1080;
  private _duration: number | null = null;
  private _fps: Framerate = Framerate.FPS_30;
  private _snapToFrame: boolean = true;

  constructor(props: Partial<Timeline> = {}) {
    // Initialize the tracks map
    if (props.tracks) {
      this._tracks = new Map(props.tracks.map((track) => [track.id, track]));
    }
    this._width = props.width ?? 1920;
    this._height = props.height ?? 1080;
    this._duration = props.duration ?? null;
    this._fps = props.fps ?? Framerate.FPS_30;
    this._snapToFrame = props.snapToFrame ?? true;

    // Calculate duration from elements if not provided
    if (this._duration === null) {
      this.recalculateDuration();
    }
  }

  // Getters for private properties
  get id(): string {
    return this._id;
  }

  get tracks(): readonly Track[] {
    return Array.from(this._tracks.values());
  }

  get tracksMap(): ReadonlyMap<string, Track> {
    return this._tracks;
  }

  get width(): number {
    return this._width;
  }

  get height(): number {
    return this._height;
  }

  get duration(): number {
    return this._duration ?? 0;
  }

  get fps(): Framerate {
    return this._fps;
  }

  get snapToFrame(): boolean {
    return this._snapToFrame;
  }

  // Public setters as methods (for MobX actions and validation)
  setWidth(width: number): void {
    if (width <= 0) {
      throw new Error('Width must be greater than 0');
    }
    this._width = width;
  }

  setHeight(height: number): void {
    if (height <= 0) {
      throw new Error('Height must be greater than 0');
    }
    this._height = height;
  }

  setDuration(duration: number | null): void {
    if (duration !== null && duration < 0) {
      throw new Error('Duration cannot be negative');
    }
    this._duration = duration;
  }

  setFps(value: Framerate): void {
    // Validate that the value is one of the supported framerates
    if (
      value !== Framerate.FPS_24 &&
      value !== Framerate.FPS_30 &&
      value !== Framerate.FPS_60
    ) {
      throw new Error(
        `Invalid framerate: ${value}. Must be one of: 24, 30, 60`,
      );
    }
    this._fps = value;
  }

  setSnapToFrame(value: boolean): void {
    this._snapToFrame = value;
  }

  /**
   * Gets all elements across all tracks
   */
  get allElements(): readonly Element[] {
    const elements = Array.from(this._tracks.values()).flatMap(
      (track) => track.elements,
    );

    // We don't modify the array in place, but we need to make it sortable for tests
    // This adds a 'sort' method to the returned array
    Object.defineProperty(elements, 'sort', {
      value: function (
        compareFn: (a: Element, b: Element) => number,
      ): Element[] {
        return [...elements].sort(compareFn);
      },
      enumerable: false,
    });

    return elements;
  }

  /**
   * Creates a new track and adds it to the timeline
   */
  createTrack(
    type: 'video' | 'audio' | 'text' | 'image' | 'composition' = 'video',
    trackIdOverride?: string, // for cases when track doesn't follow an order, example: stacked layout
  ): Track {
    const trackId = trackIdOverride ?? String(this.getNextTrackId());
    const track = new Track({ id: trackId, type });

    // Add to the tracks map
    this._tracks.set(trackId, track);

    return track;
  }

  /**
   * Adds a track with given properties to the timeline (for compatibility with tests)
   */
  addTrack(props: {
    name: string;
    type?: 'video' | 'audio' | 'text' | 'image' | 'composition';
  }): string {
    const type = props.type || 'video';
    const track = this.createTrack(type);
    return track.id;
  }

  /**
   * Adds an element to a track (for compatibility with tests)
   * TODO: Remove this and prefer object based approach?
   */
  addElementToTrack(trackId: string, element: Element): boolean {
    return this.addElement(trackId, element);
  }

  /**
   * Gets the next available track ID
   * Track IDs must be below 32 as 32+ are reserved for internal work
   */
  private getNextTrackId(): number {
    if (this._tracks.size === 0) {
      return 1;
    }

    // Convert string IDs to numbers, filter out reserved IDs (32+), and get available IDs
    const trackIds = Array.from(this._tracks.keys())
      .map((id) => Number(id))
      .filter((id) => !isNaN(id) && id < 32); // Only consider valid IDs below 32

    // Find the first available ID from 1 to 31
    for (let i = 1; i < 32; i++) {
      if (!trackIds.includes(i)) {
        return i;
      }
    }

    // If all IDs from 1-31 are taken, throw an error
    throw new Error(
      'No available track IDs below 32. Maximum number of tracks (31) reached.',
    );
  }

  /**
   * Adds an existing Track instance to the timeline
   * @param track The pre-configured Track instance to add
   * @returns The added track for method chaining
   * @throws Error if a track with the same ID already exists
   */
  addExistingTrack(track: Track): Track {
    // Validate that the track ID is valid
    const trackIdNum = Number(track.id);
    if (isNaN(trackIdNum) || trackIdNum < 1 || trackIdNum > 31) {
      throw new Error(
        `Invalid track ID: ${track.id}. Track IDs must be between 1 and 31.`,
      );
    }

    // Check if a track with this ID already exists
    if (this._tracks.has(track.id)) {
      throw new Error(`A track with ID ${track.id} already exists.`);
    }

    // Add the track to the map
    this._tracks.set(track.id, track);

    // Recalculate duration since we added a track that might have elements
    this.recalculateDuration();

    return track;
  }

  /**
   * Adds an element to a track
   */
  addElement(trackId: string, element: Element): boolean {
    const track = this.getTrack(trackId);
    if (!track) return false;

    track.addElement(element);
    this.recalculateDuration();
    return true;
  }

  /**
   * Updates an element's properties
   */
  updateElement(elementId: string, props: Partial<Element>): boolean {
    let success = false;

    for (const track of this._tracks.values()) {
      if (track.updateElement(elementId, props)) {
        success = true;
        break;
      }
    }

    if (success) this.recalculateDuration();
    return success;
  }

  /**
   * Removes an element from the timeline
   */
  removeElement(elementId: string): boolean {
    let success = false;

    for (const track of this._tracks.values()) {
      if (track.removeElement(elementId)) {
        success = true;
        break;
      }
    }

    if (success) this.recalculateDuration();
    return success;
  }

  /**
   * Replaces an element with a new element while preserving position and track
   * This is used for element type transitions (e.g., ImageElement ↔ CompositionElement)
   */
  replaceElement(elementId: string, newElement: Element): boolean {
    let success = false;

    for (const track of this._tracks.values()) {
      if (track.replaceElement(elementId, newElement)) {
        success = true;
        break;
      }
    }

    if (success) this.recalculateDuration();
    return success;
  }

  /**
   * Recalculates the timeline duration based on the elements
   * Adjusts the timeline length based on the furthest element end time
   */
  recalculateDuration(): void {
    const elements = this.allElements;
    if (elements.length === 0) {
      this._duration = null;
      return;
    }

    // Calculate the end time of each element (time + duration)
    const endTimes = elements.map((el) => {
      const time = el.time || 0;
      // Handle different duration types, ensuring we get a valid number
      let duration = 0;
      if (typeof el.duration === 'number') {
        duration = el.duration;
      } else if (el.duration === 'media') {
        // For 'media' duration, we'll default to 0 for now since metadata system was removed
        const mediaDuration = 0;
        duration = mediaDuration;
      }
      return time + duration;
    });

    // Find the furthest end time, ensuring a minimum duration
    const lastEndTime = Math.max(...endTimes, 0);

    // Round to the nearest 0.01 seconds to avoid tiny decimal differences
    // but maintain sufficient precision
    this._duration = Math.ceil(lastEndTime * 100) / 100;
  }

  /**
   * Converts the timeline to JSON compatible with Creatomate
   */
  toJSON(formatTime: Boolean = false): Record<string, any> {
    return {
      width: this._width,
      height: this._height,
      elements: this.allElements.map((element) => element.toJSON(formatTime)),
      frame_rate: `${this._fps} fps`,
      output_format: 'mp4',
    };
  }

  /**
   * Creates a Timeline from the Creatomate's JSON state
   */
  static fromJSON(json: Record<string, any>): Timeline {
    const elements = json.elements ?? [];
    const elementsByTrack = new Map<number, Element[]>();

    // Group elements by track
    elements.forEach((elementJson: Record<string, any>) => {
      const trackId = elementJson.track ?? 1; // Default to track 1 if null
      if (!elementsByTrack.has(trackId)) elementsByTrack.set(trackId, []);

      let element: Element;
      switch (elementJson.type) {
        case 'video':
          element = VideoElement.fromJSON(elementJson);
          break;
        case 'text':
          element = TextElement.fromJSON(elementJson);
          break;
        case 'audio':
          element = AudioElement.fromJSON(elementJson);
          break;
        case 'image':
          element = ImageElement.fromJSON(elementJson);
          break;
        case 'composition':
          element = CompositionElement.fromJSON(elementJson);
          break;
        case 'shape':
          element = ShapeElement.fromJSON(elementJson);
          break;
        default:
          element = Element.fromJSON(elementJson);
      }
      elementsByTrack.get(trackId)!.push(element);
    });

    // Create tracks as an array first (for constructor compatibility)
    const tracks: Track[] = [];

    // Process each track
    for (const [trackId, trackElements] of elementsByTrack.entries()) {
      // Get first element's type, default to 'video' if not available
      const elementType = trackElements[0]?.type ?? 'video';
      // Ensure the type is one of the allowed types for Track
      const trackType =
        elementType === 'video' ||
        elementType === 'text' ||
        elementType === 'audio' ||
        elementType === 'image'
          ? (elementType as
              | 'video'
              | 'text'
              | 'audio'
              | 'image'
              | 'composition')
          : 'video';

      tracks.push(
        new Track({
          id: String(trackId),
          type: trackType,
          elements: trackElements,
        }),
      );
    }

    // Read FPS and snapToFrame settings from the JSON if available
    let fps = Framerate.FPS_30;
    if (json.fps !== undefined) {
      // Validate that the FPS is one of our supported Framerate enum values
      const fpsNumber = Number(json.fps);
      if (
        fpsNumber === Framerate.FPS_24 ||
        fpsNumber === Framerate.FPS_30 ||
        fpsNumber === Framerate.FPS_60
      ) {
        fps = fpsNumber;
      }
    }

    return new Timeline({
      tracks,
      width: json.width ?? 1920,
      height: json.height ?? 1080,
      duration: json.duration ?? null,
      fps: fps,
      snapToFrame: json.snapToFrame ?? true,
    });
  }

  /**
   * Helper to create a new element based on type
   */
  static createElement(type: string, props: Record<string, any> = {}): Element {
    switch (type) {
      case 'video':
        return new VideoElement(props);
      case 'text':
        return new TextElement(props);
      case 'audio':
        return new AudioElement(props);
      case 'image':
        return new ImageElement(props);
      case 'composition':
        return new CompositionElement(props);
      case 'shape':
        return new ShapeElement(props);
      default:
        return new Element({ ...props, type });
    }
  }

  /**
   * Removes a track by ID
   */
  removeTrack(trackId: string): boolean {
    const hasTrack = this._tracks.has(trackId);

    if (hasTrack) {
      this._tracks.delete(trackId);
      this.recalculateDuration();
    }

    return hasTrack;
  }

  /**
   * Gets a track by ID
   */
  getTrack(trackId: string): Track | undefined {
    return this._tracks.get(trackId);
  }

  /**
   * Gets an element by ID from any track
   */
  getElement(elementId: string): Element | undefined {
    for (const track of this._tracks.values()) {
      const element = track.elements.find((el) => el.id === elementId);
      if (element) return element;
    }
    return undefined;
  }

  /**
   * Gets the track that contains the element
   */
  getTrackForElement(elementId: string): Track | undefined {
    for (const track of this._tracks.values()) {
      if (track.elements.some((element) => element.id === elementId)) {
        return track;
      }
    }
    return undefined;
  }

  /**
   * Gets the track ID that contains the element
   */
  getTrackIdForElement(elementId: string): string | undefined {
    const track = this.getTrackForElement(elementId);
    return track ? track.id : undefined;
  }

  /**
   * Moves an element to a different track
   */
  moveElementToTrack(elementId: string, targetTrackId: string): boolean {
    // Find the element in any track
    const element = this.getElement(elementId);
    if (!element) return false;

    // Remove it from its current track
    if (!this.removeElement(elementId)) return false;

    // Add it to the target track
    return this.addElement(targetTrackId, element);
  }

  /**
   * Convert time in seconds to frame number
   * @param time Time in seconds
   * @returns Frame number (integer)
   */
  timeToFrame(time: number): number {
    return Math.round(time * this._fps);
  }

  /**
   * Convert frame number to time in seconds
   * @param frame Frame number
   * @returns Time in seconds
   */
  frameToTime(frame: number): number {
    return frame / this._fps;
  }

  /**
   * Snap time to nearest frame boundary if enabled. This is probably a net net negative
   * as it will cause the time to be rounded to the nearest frame but Creatomate truncates
   * the time to 4 during render.
   * @param time Time in seconds
   * @returns Time in seconds, snapped to the nearest frame boundary if snapToFrame is enabled
   */
  snapTimeToFrame(time: number): number {
    if (!this._snapToFrame) return time;
    const frame = this.timeToFrame(time);
    return this.frameToTime(frame);
  }

  /**
   * Gets the first video track from the timeline, assuming there is one
   * @returns The first video track or undefined if none exists
   */
  getVideoTrack(): Track | undefined {
    for (const track of this._tracks.values()) {
      if (track.type === 'video') {
        return track;
      }
    }
    return undefined;
  }

  /**
   * Sorts the tracks array based on their numerical IDs
   * Lower IDs will appear first in the array (typically representing lower layers)
   * Higher IDs will appear later (typically representing upper layers)
   * @param descending If true, sorts in descending order (higher IDs first)
   * @returns The number of tracks that were sorted
   */
  sortTracksByNumericId(descending: boolean = false): number {
    // Convert tracks map to array for sorting
    const tracksArray = Array.from(this._tracks.values());

    if (tracksArray.length <= 1) {
      return 0; // Nothing to sort
    }

    // Sort tracks by numeric ID
    tracksArray.sort((a, b) => {
      const aId = Number(a.id);
      const bId = Number(b.id);

      // Handle non-numeric IDs by placing them at the end
      if (isNaN(aId) && isNaN(bId)) return 0;
      if (isNaN(aId)) return 1;
      if (isNaN(bId)) return -1;

      // Sort numerically
      return descending ? bId - aId : aId - bId;
    });

    // Rebuild the tracks map with the sorted array to maintain order
    this._tracks = new Map(tracksArray.map((track) => [track.id, track]));

    return tracksArray.length;
  }
}
